/**
 * Permission onboarding — the most fragile fifteen seconds in the product.
 *
 * Two explicit steps, each behind its own tap (`ProductOverview.md` §4.2), because iOS will
 * only grant device orientation from inside a user gesture and because two prompts fired at
 * once is how people end up denying both.
 *
 * Every denial leads somewhere useful. Location refused is fatal and says so plainly, with the
 * per-platform way to undo it. Compass refused is not fatal at all: it drops through to the
 * north-up view, which is genuinely usable.
 */

import { el } from '../../lib/dom';
import { markLogo } from '../../ui/mark';
import { platformHint, type LocationPermission } from '../../sensors/location';
import type { SensorHub } from '../../sensors';

export type OnboardingOutcome = 'ready' | 'no-compass' | 'blocked' | 'later';

export interface OnboardingOptions {
  sensors: SensorHub;
  /** Who they are meeting, if we know — this screen is where the promise gets made. */
  peerName: string | null;
  /**
   * Offer a way back out without answering.
   *
   * True only when nothing is waiting on the sensors — a host who tapped "set me up" from the
   * share screen while still holding an unsent link. Nobody who is about to be handed an arrow
   * gets this door, because for them the question is not optional, it *is* the next screen.
   */
  allowLater?: boolean;
  onDone: (outcome: OnboardingOutcome) => void;
}

interface StepOptions {
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void | Promise<void>;
  busyLabel: string;
  skipLabel?: string;
  onSkip?: () => void;
  note?: string;
}

function step(options: StepOptions): HTMLElement {
  const button = el(
    'button',
    { class: 'button button--primary button--block', type: 'button' },
    options.actionLabel,
  );

  const note = el('p', { class: 'onboarding__note', role: 'status' }, options.note ?? '');

  let busy = false;
  button.addEventListener('click', () => {
    if (busy) return;
    busy = true;
    button.setAttribute('disabled', '');
    button.textContent = options.busyLabel;

    void Promise.resolve(options.onAction()).finally(() => {
      busy = false;
      button.removeAttribute('disabled');
      button.textContent = options.actionLabel;
    });
  });

  return el(
    'main',
    { class: 'screen screen--centered onboarding' },
    markLogo(52),
    el('h1', { class: 'screen__title', text: options.title }),
    el('p', { class: 'screen__body', text: options.body }),
    el(
      'div',
      { class: 'screen__actions' },
      button,
      options.skipLabel &&
        el(
          'button',
          {
            class: 'button button--ghost button--block',
            type: 'button',
            onclick: () => options.onSkip?.(),
          },
          options.skipLabel,
        ),
    ),
    note,
  );
}

const LOCATION_PROBLEM: Record<LocationPermission, string> = {
  denied: 'Location is blocked for this page.',
  unavailable: 'Your device could not get a fix. Step outside if you can.',
  timeout: 'No signal yet. Step outside if you can, then try again.',
  unknown: 'Location did not work. Try again.',
  granted: '',
};

/**
 * Mounts the onboarding flow into a container and calls back with what the user ended up with.
 * Returns a teardown.
 */
export function mountOnboarding(container: HTMLElement, options: OnboardingOptions): () => void {
  let disposed = false;

  const show = (node: HTMLElement): void => {
    if (disposed) return;
    container.replaceChildren(node);
  };

  /* --- step two: the compass ------------------------------------------- */

  const compassStep = (): void => {
    show(
      step({
        title: 'Now the compass',
        body: 'The arrow needs to know which way you are facing, not just where you are.',
        actionLabel: 'Enable compass',
        busyLabel: 'Waiting…',
        skipLabel: 'Skip — show me a north-up view',
        onSkip: () => options.onDone('no-compass'),
        onAction: async () => {
          const result = await options.sensors.requestHeading();
          if (disposed) return;

          // 'unknown' means no permission call exists — Android grants it by listening.
          if (result === 'granted' || result === 'unknown') {
            options.onDone('ready');
            return;
          }

          // Denied or unsupported is not a dead end: the north-up view still works.
          options.onDone('no-compass');
        },
      }),
    );
  };

  /* --- location refused ------------------------------------------------- */

  /**
   * Refused, and not a dead end.
   *
   * This screen used to offer "try again" and "reload" and nothing else, so a denial ended the
   * session then and there — including for the person who had not yet sent anybody the link.
   * Losing the arrow is bad; losing the ability to hand over a link, extend the session or set a
   * meeting point because of it is absurd. So there is a way onward, and the screens past it say
   * plainly what is missing rather than pretending.
   */
  const blockedScreen = (permission: LocationPermission): void => {
    show(
      el(
        'main',
        { class: 'screen screen--centered onboarding' },
        el('h1', { class: 'screen__title', text: 'Beeline needs your location' }),
        el('p', {
          class: 'screen__body',
          text: `${LOCATION_PROBLEM[permission]} Without it there is nothing to point at, and pretending otherwise would be worse than saying so.`,
        }),
        el('p', { class: 'onboarding__hint', text: platformHint() }),
        el(
          'div',
          { class: 'screen__actions' },
          el(
            'button',
            {
              class: 'button button--primary button--block',
              type: 'button',
              onclick: () => locationStep(),
            },
            'Try again',
          ),
          el(
            'button',
            {
              class: 'button button--ghost button--block',
              type: 'button',
              'data-action': 'continue-blocked',
              onclick: () => options.onDone('blocked'),
            },
            'Carry on without it',
          ),
        ),
      ),
    );
  };

  /* --- step one: location ----------------------------------------------- */

  const locationStep = (): void => {
    const who = options.peerName ? `${options.peerName} needs` : 'They need';

    show(
      step({
        title: 'Two taps and you are walking',
        body: `${who} to see which way you are, and you need the same. Your location goes straight to them and is never written down.`,
        actionLabel: 'Allow location',
        busyLabel: 'Looking for a signal…',
        skipLabel: options.allowLater ? 'Not just yet' : undefined,
        onSkip: () => options.onDone('later'),
        onAction: async () => {
          const { permission } = await options.sensors.requestLocation();
          if (disposed) return;

          if (permission === 'granted') {
            compassStep();
            return;
          }
          blockedScreen(permission);
        },
      }),
    );
  };

  locationStep();

  return () => {
    disposed = true;
  };
}
